import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { IntegrityMonitorRuleSetFormWebComponentSchema, RuleSetData } from '../types';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { Subscription } from 'rxjs';
import { InputEvents } from '../../../../ui/gzInput/types';
import { TemplateState } from '../../../../types/common/types';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { RemoteValidator } from '../../../../ui/gzForm/validators/remote.validator';
import { MaxLengthValidator } from '../../../../ui/gzForm/validators/max-length.validator';
import { IMConstants } from '../../../common/utils/IMConstants';
import { RegexValidator } from '../../../../ui/gzForm/validators/regex.validator';
import { MinLengthValidator } from '../../../../ui/gzForm/validators/min-length.validator';

export class IntegrityMonitorRuleSetFormView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  private formGroupSubscription: Subscription | null = null;

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$formWrapper = this.shadow.querySelector('.form-wrapper');
    this.dom.$formTitle = this.shadow.querySelector('#form-title');
    this.dom.$ruleSetNameFormField = this.shadow.querySelector('#rule-set-name-form-field');
    this.dom.$ruleSetDescriptionFormField = this.shadow.querySelector('#rule-set-description-form-field');
    this.dom.$saveButton = this.shadow.querySelector('#saveButton');
    this.dom.$cancelButton = this.shadow.querySelector('#cancelButton');

    this.setLoadingState();
    this.getTranslations();
    this.setTranslatedStrings();

    // Remove any white spaces before/after the rule set name
    this.dom.$ruleSetNameFormField.Input.addEventListener(InputEvents.BLUR, () => {
      this.dom.$ruleSetNameFormField.Input.setFormValue(this.dom.$ruleSetNameFormField.Input.getFormValue().trim());
    });
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.translations = this.stringsTranslator.getIntegrityMonitorStrings().integrityMonitorRules.ruleSetForm;
  }

  setTranslatedStrings() {
    this.dom.$ruleSetNameFormField.label = this.translations.name;
    this.dom.$ruleSetNameFormField.Input.placeholder = this.translations.namePlaceholder;
    this.dom.$ruleSetDescriptionFormField.label = this.translations.description;
    this.dom.$ruleSetDescriptionFormField.Input.placeholder = this.translations.descriptionPlaceholder;
    this.dom.$saveButton.innerText = this.translations.save;
    this.dom.$cancelButton.innerText = this.commonStrings.cancel;
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  buildSuccessView(data: IntegrityMonitorRuleSetFormWebComponentSchema) {
    this.setFormTitle(data.ruleSetData);
    this.createFormGroup(data);
    this.resetFields();
    this.setFormData(data.ruleSetData);

    // Enable / disable "Save" button based on formGroup's validity
    this.formGroupSubscription = this.formGroup.valid$.subscribe({
      next: valid => {
        this.dom.$saveButton.disabled = !valid;
      },
    });

    this.removeLoadingState();
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public resetFields() {
    // Reset fields state when rendering AGAIN the component (e.g. close and open the form again)
    this.dom.$ruleSetNameFormField.resetField();
    this.dom.$ruleSetDescriptionFormField.resetField();
  }

  private setFormTitle(ruleSetData?: RuleSetData) {
    this.dom.$formTitle.innerText = ruleSetData ? this.translations.editRuleSet : this.translations.newRuleSet;
  }

  private createFormGroup(data: IntegrityMonitorRuleSetFormWebComponentSchema) {
    this.dom.$ruleSetNameFormField.Validators = [
      RequiredValidator(this.translations.nameIsRequired),
      MinLengthValidator(IMConstants.MIN_LENGTH_RULE_SET_NAME, this.translations.minLengthName),
      MaxLengthValidator(IMConstants.MAX_LENGTH_RULE_SET_NAME, this.translations.maxLengthName),
      RegexValidator(IMConstants.REGEX_NO_SPECIAL_CHARACTERS, this.translations.specialCharactersNotPermitted),
      RemoteValidator(
        (inputValue: string) =>
          data.uniqueRuleSetNameValidator(
            inputValue,
            this.dependencies.getActiveCompany().companyId,
            data.ruleSetData?._id
          ),
        (response: { success: boolean }) => response.success,
        this.translations.nameAlreadyExists,
        this.translations.communicationError
      ),
    ];

    this.dom.$ruleSetDescriptionFormField.Validators = [
      MaxLengthValidator(IMConstants.MAX_LENGTH_RULE_SET_DESCRIPTION, this.translations.maxLengthDescription),
      RegexValidator(IMConstants.REGEX_NO_SPECIAL_CHARACTERS, this.translations.specialCharactersNotPermitted),
    ];

    this.formGroup = new FormGroup({
      name: this.dom.$ruleSetNameFormField,
      description: this.dom.$ruleSetDescriptionFormField,
    });
  }

  public setFormData(ruleSetData?: RuleSetData) {
    // Initialise the form data depending on the Create / Edit context
    this.formGroup.value = {
      name: ruleSetData ? ruleSetData.name : '',
      description: ruleSetData ? ruleSetData.description : '',
    };
  }

  protected onDisconnect() {
    this.unsubscribeObservers();
  }

  public unsubscribeObservers() {
    this.formGroup?.unsubscribe();
    this.formGroupSubscription?.unsubscribe();
  }
}
